import React, { useEffect, useState } from 'react';

import { ErrorAlert, SuccessAlert } from 'components/Alert';
import Input from 'components/inputs/Input';
import { ErrorSpan } from 'components/inputs/UploadFile/StyleUploadFile';
import Spinner from 'components/Spinner/Spinner';
import iconEmail from 'images/icons/email.png';
import SectionWrapper from 'pages/Layout/SectionWrapper';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { selectUser } from 'redux/slices/userSlice';
import { getReport } from 'services/rest/Report/getReport';
import Swal from 'sweetalert2';

import {
  ContentBlock,
  BackgroundImg,
  CheckboxContainer,
  MarginButtonDownload,
  MarginDescription,
  SectionCard,
  TextCheckbox,
  FiltersForm
} from './Styled';
import DropdownSelect from 'components/inputs/DropdownSelect/DropdownSelect';
import PeriodSelector, {
  ALL_TIME_PERIOD,
  isPeriodComplete,
  ReportPeriodValue
} from 'components/inputs/PeriodSelector/PeriodSelector';
import moment from 'moment';
import { selectCompany } from 'redux/slices/Company/CompanySlice';

const PERIOD_REPORT_TYPES = [
  'AMBASSADORS',
  'PLATFORM',
  'CREATOR',
  'BONUS'
] as const;
type PeriodReportType = typeof PERIOD_REPORT_TYPES[number];

const isPeriodReport = (type: string): type is PeriodReportType =>
  (PERIOD_REPORT_TYPES as readonly string[]).includes(type);

const ReportsAdminPage = () => {
  const { t } = useTranslation(['group_page', 'report_page', 'dates']);
  const [successAlertVisible, setSuccessAlertVisible] = useState(false);
  const [updateError, setUpdateError] = useState<string | null>(null);
  const emailPlatform = '';
  const [emailEmployee, setEmailEmployee] = useState('');
  const [validEmailEmployee, setValidEmailEmployee] = useState(false);
  const emailParticipation = '';
  const [check, setCheck] = useState(false);
  const emailRegex = /^[^\s@]+@[^\s@]+\.[a-zA-Z]{2,}$/;
  const [loader, setLoader] = useState({
    PLATFORM: false,
    AMBASSADORS: false,
    CREATOR: false,
    EMPLOYEES: false,
    PREVIEW_EMPLOYEE: false,
    BONUS: false,
    PARTICIPATIONS_BY_DATE: false,
    MONTHLY_AMBASSADORS: false,
    CONSOLIDATED: false
  });
  const { currentUser } = useSelector(selectUser);
  const { company } = useSelector(selectCompany);

  const currentMonth = moment().format('M') as any;
  const currentYear = moment().year();
  const monthsData = t('dates:month', {
    returnObjects: true,
    defaultValue: []
  });

  // Función para obtener los meses disponibles según el año
  const getAvailableMonths = (year: number) => {
    const allMonths = Array.isArray(monthsData)
      ? monthsData.map((month, index) => ({
          label: month,
          value: index + 1
        }))
      : [];

    // Si es el año actual, filtrar solo hasta el mes actual
    // Si es un año pasado, mostrar todos los meses
    return year === currentYear
      ? allMonths.filter(month => month.value <= currentMonth)
      : allMonths;
  };

  const months = getAvailableMonths(currentYear);
  const years = [];
  for (let i = 0; i < 5; i++) {
    const yearValue = currentYear - i;
    years.push({ label: yearValue.toString(), value: yearValue });
  }
  const consolidatedTypes = [
    { label: t('report_page:a_month'), value: 'month' },
    { label: t('report_page:several_months'), value: 'months' }
  ];
  const [year, setYear] = useState<any>(years[0]);
  const [month, setMonth] = useState<any>(months[currentMonth - 1]);
  const [yearAmbassador, setYearAmbassador] = useState<any>(years[0]);
  const [monthAmbassador, setMonthAmbassador] = useState<any>(
    months[currentMonth - 1]
  );
  const [startMonthCon, setStartMonthCon] = useState<any>(
    months[currentMonth - 1]
  );
  const [endMonthCon, setEndtMonthCon] = useState<any>(months[currentMonth]);
  const [periods, setPeriods] = useState<
    Record<PeriodReportType, ReportPeriodValue>
  >({
    AMBASSADORS: ALL_TIME_PERIOD,
    PLATFORM: ALL_TIME_PERIOD,
    CREATOR: ALL_TIME_PERIOD,
    BONUS: ALL_TIME_PERIOD
  });
  const onChangePeriod = (type: PeriodReportType) => (
    value: ReportPeriodValue
  ) => setPeriods(previous => ({ ...previous, [type]: value }));

  useEffect(() => {
    const availableMonths = getAvailableMonths(yearAmbassador.value);
    const isMonthValid = availableMonths.some(
      m => m.value === monthAmbassador.value
    );
    if (!isMonthValid && availableMonths.length > 0) {
      setMonthAmbassador(availableMonths[availableMonths.length - 1]);
    }
  }, [yearAmbassador]);

  useEffect(() => {
    const availableMonths = getAvailableMonths(year.value);
    const isMonthValid = availableMonths.some(m => m.value === month.value);
    if (!isMonthValid && availableMonths.length > 0) {
      setMonth(availableMonths[availableMonths.length - 1]);
    }
  }, [year]);

  // Estados para la modal
  const [modalYear, setModalYear] = useState<any>(years[0]);
  const [modalPeriodType, setModalPeriodType] = useState<any>(
    consolidatedTypes[0]
  );
  const [modalStartMonth, setModalStartMonth] = useState<any>(
    months[currentMonth - 1]
  );
  const [modalEndMonth, setModalEndMonth] = useState<any>(
    months[currentMonth - 1]
  );

  const showReportModal = () => {
    // Resetear estados de la modal
    setModalYear(years[0]);
    setModalPeriodType(consolidatedTypes[0]);
    setModalStartMonth(months[currentMonth - 1]);
    setModalEndMonth(months[currentMonth - 1]);

    Swal.fire({
      title: t('report_page:download_report'),
      html: `
        <style>
          .custom-dropdown {
            position: relative;
            display: inline-block;
            width: 100%;
          }
          
          .dropdown-button {
            display: flex;
            align-items: center;
            justify-content: space-between;
            width: 100%;
            padding: 12px 16px;
            background-color: #f8f9fa;
            border: 1px solid #dee2e6;
            border-radius: 8px;
            cursor: pointer;
            transition: all 0.2s ease;
            font-size: 14px;
            color: #495057;
          }
          
          .dropdown-button:hover {
            background-color: #e9ecef;
            border-color: #adb5bd;
          }
          
          .dropdown-button.active {
            background-color: #e3f2fd;
            border-color: #1976d2;
          }
          
          .dropdown-arrow {
            width: 16px;
            height: 16px;
            transition: transform 0.2s ease;
          }
          
          .dropdown-button.active .dropdown-arrow {
            transform: rotate(180deg);
          }
          
          .dropdown-menu {
            position: fixed;
            background-color: white;
            border: 1px solid #dee2e6;
            border-radius: 8px;
            box-shadow: 0 8px 24px rgba(0, 0, 0, 0.25);
            z-index: 10000;
            max-height: 200px;
            overflow-y: auto;
            display: none;
            min-width: 200px;
          }
          
          .dropdown-menu.show {
            display: block;
          }
          
          .dropdown-item {
            padding: 12px 16px;
            cursor: pointer;
            border-bottom: 1px solid #f8f9fa;
            font-size: 14px;
            color: #495057;
            transition: background-color 0.2s ease;
          }
          
          .dropdown-item:last-child {
            border-bottom: none;
          }
          
          .dropdown-item:hover {
            background-color: #f8f9fa;
          }
          
          .dropdown-item.selected {
            background-color: #e3f2fd;
            color: #1976d2;
            font-weight: 500;
          }
          
          .dropdown-item.selected:hover {
            background-color: #bbdefb;
          }

          .swal2-actions {
            z-index: unset;
          }
        </style>
        <div style="text-align: left; padding: 20px;">
          <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 20px; align-items: center; margin-bottom: 20px;">
            <label style="font-weight: 500; color: #333; margin: 0;">
              ${t('report_page:select_year')}
            </label>
            <div class="custom-dropdown" data-name="year">
              <div class="dropdown-button" data-dropdown="yearSelect">
                <span class="dropdown-text">${years[0].label}</span>
                <svg class="dropdown-arrow" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <polyline points="6,9 12,15 18,9"></polyline>
                </svg>
              </div>
              <div class="dropdown-menu" id="yearDropdown">
                ${years
                  .map(
                    (year, index) =>
                      `<div class="dropdown-item ${
                        index === 0 ? 'selected' : ''
                      }" data-value="${year.value}">${year.label}</div>`
                  )
                  .join('')}
              </div>
            </div>
          </div>
          
          <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 20px; align-items: center; margin-bottom: 20px;">
            <label style="font-weight: 500; color: #333; margin: 0;">
              ${t('report_page:select_period')}
            </label>
            <div class="custom-dropdown" data-name="periodType">
              <div class="dropdown-button" data-dropdown="periodTypeSelect">
                <span class="dropdown-text">${consolidatedTypes[0].label}</span>
                <svg class="dropdown-arrow" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <polyline points="6,9 12,15 18,9"></polyline>
                </svg>
              </div>
              <div class="dropdown-menu" id="periodTypeDropdown">
                ${consolidatedTypes
                  .map(
                    (type, index) =>
                      `<div class="dropdown-item ${
                        index === 0 ? 'selected' : ''
                      }" data-value="${type.value}">${type.label}</div>`
                  )
                  .join('')}
              </div>
            </div>
          </div>
          
          <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 20px; align-items: center; margin-bottom: 20px;">
            <label id="selectMonthLabel" style="font-weight: 500; color: #333; margin: 0; display: block">
              ${t('report_page:select_month')}
            </label>
            <label id="startMonthLabel" style="font-weight: 500; color: #333; margin: 0; display: none">
              ${t('report_page:start_month')}
            </label>
            <div class="custom-dropdown" data-name="startMonth">
              <div class="dropdown-button" data-dropdown="startMonthSelect">
                <span class="dropdown-text">${modalStartMonth.label}</span>
                <svg class="dropdown-arrow" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <polyline points="6,9 12,15 18,9"></polyline>
                </svg>
              </div>
              <div class="dropdown-menu" id="startMonthDropdown">
                ${months
                  .map(
                    month =>
                      `<div class="dropdown-item ${
                        month.value === modalStartMonth.value ? 'selected' : ''
                      }" data-value="${month.value}">${month.label}</div>`
                  )
                  .join('')}
              </div>
            </div>
          </div>
          
          <div id="endMonthContainer" style="display: none;">
            <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 20px; align-items: center; margin-bottom: 20px;">
              <label style="font-weight: 500; color: #333; margin: 0;">
                ${t('report_page:end_month')}
              </label>
              <div class="custom-dropdown" data-name="endMonth">
                <div class="dropdown-button" data-dropdown="endMonthSelect">
                  <span class="dropdown-text">${modalEndMonth.label}</span>
                  <svg class="dropdown-arrow" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <polyline points="6,9 12,15 18,9"></polyline>
                  </svg>
                </div>
                <div class="dropdown-menu" id="endMonthDropdown">
                  ${months
                    .map(
                      month =>
                        `<div class="dropdown-item ${
                          month.value === modalEndMonth.value ? 'selected' : ''
                        }" data-value="${month.value}">${month.label}</div>`
                    )
                    .join('')}
                </div>
              </div>
            </div>
          </div>
        </div>
      `,
      showCancelButton: true,
      confirmButtonText: t('report_page:download'),
      confirmButtonColor: '#1976d2',
      cancelButtonText: t('report_page:cancel'),
      cancelButtonColor: '#757575',
      reverseButtons: true,
      width: '600px',
      didOpen: () => {
        // Variables para almacenar los valores seleccionados
        const selectedValues = {
          year: years[0],
          periodType: consolidatedTypes[0],
          startMonth: modalStartMonth,
          endMonth: modalEndMonth
        };

        // Función para obtener los meses disponibles según el año seleccionado
        const getAvailableMonthsForYear = (year: number) => {
          const allMonths = Array.isArray(monthsData)
            ? monthsData.map((month, index) => ({
                label: month,
                value: index + 1
              }))
            : [];

          // Si es el año actual, filtrar solo hasta el mes actual
          // Si es un año pasado, mostrar todos los meses
          return year === currentYear
            ? allMonths.filter(month => month.value <= currentMonth)
            : allMonths;
        };

        // Función para cerrar todos los dropdowns
        const closeAllDropdowns = () => {
          document.querySelectorAll('.dropdown-menu').forEach(menu => {
            menu.classList.remove('show');
          });
          document.querySelectorAll('.dropdown-button').forEach(button => {
            button.classList.remove('active');
          });
        };

        // Función para manejar clicks en los botones de dropdown
        const handleDropdownClick = (button: HTMLElement, menuId: string) => {
          const menu = document.getElementById(menuId);
          if (!menu) return;

          const isOpen = menu.classList.contains('show');

          closeAllDropdowns();

          if (!isOpen) {
            // Calcular posición del dropdown
            const rect = button.getBoundingClientRect();
            const menuHeight = 200; // max-height del menu
            const spaceBelow = window.innerHeight - rect.bottom;
            const spaceAbove = rect.top;

            // Decidir si abrir hacia arriba o hacia abajo
            if (spaceBelow >= menuHeight || spaceBelow >= spaceAbove) {
              // Abrir hacia abajo
              menu.style.top = `${rect.bottom + 4}px`;
            } else {
              // Abrir hacia arriba
              menu.style.top = `${rect.top - menuHeight - 4}px`;
            }

            menu.style.left = `${rect.left}px`;
            menu.style.width = `${rect.width}px`;
            menu.classList.add('show');
            button.classList.add('active');
          }
        };

        // Función para actualizar un dropdown
        const updateDropdown = (
          dropdownName: string,
          newValue: number | string,
          newText: string
        ) => {
          const button = document.querySelector(
            `[data-dropdown="${dropdownName}"]`
          ) as HTMLElement;
          if (!button) return;

          const text = button.querySelector('.dropdown-text');
          if (text) {
            text.textContent = newText;
          }

          // Actualizar clases selected
          const menu = button.nextElementSibling as HTMLElement;
          if (menu) {
            menu.querySelectorAll('.dropdown-item').forEach(item => {
              item.classList.remove('selected');
              if (item.getAttribute('data-value') === newValue.toString()) {
                item.classList.add('selected');
              }
            });
          }
        };

        // Función para actualizar las opciones de meses de inicio basado en el año
        const updateStartMonthOptions = (yearValue: number) => {
          const availableMonths = getAvailableMonthsForYear(yearValue);
          const startMonthDropdown = document.getElementById(
            'startMonthDropdown'
          );
          if (!startMonthDropdown) return;

          // Seleccionar el primer mes disponible o mantener el actual si está disponible
          const newStartMonth =
            availableMonths.find(
              m => m.value === selectedValues.startMonth.value
            ) || availableMonths[0];

          startMonthDropdown.innerHTML = availableMonths
            .map(
              month =>
                `<div class="dropdown-item ${
                  month.value === newStartMonth.value ? 'selected' : ''
                }" data-value="${month.value}">${month.label}</div>`
            )
            .join('');

          selectedValues.startMonth = newStartMonth;
          updateDropdown(
            'startMonthSelect',
            newStartMonth.value,
            newStartMonth.label
          );

          // Reattach event listeners
          startMonthDropdown
            .querySelectorAll('.dropdown-item')
            .forEach(item => {
              item.addEventListener('click', () => {
                const value = parseInt(item.getAttribute('data-value') || '0');
                const month = availableMonths.find(m => m.value === value);
                if (month) {
                  selectedValues.startMonth = month;
                  updateDropdown('startMonthSelect', value, month.label);
                  if (selectedValues.periodType.value === 'months') {
                    updateEndMonthOptions(availableMonths);
                  }
                  closeAllDropdowns();
                }
              });
            });

          // Si está en modo múltiples meses, actualizar también el mes final
          if (selectedValues.periodType.value === 'months') {
            updateEndMonthOptions(availableMonths);
          }
        };

        // Función para actualizar opciones del mes final
        const updateEndMonthOptions = (availableMonths?: any[]) => {
          const monthsToUse =
            availableMonths ||
            getAvailableMonthsForYear(selectedValues.year.value);
          const startMonthValue = selectedValues.startMonth.value;
          const filteredMonths = monthsToUse.filter(
            m => m.value >= startMonthValue
          );

          const endMonthDropdown = document.getElementById('endMonthDropdown');
          if (!endMonthDropdown) return;

          // Seleccionar el primer mes disponible o mantener el actual si está disponible
          const newEndMonth =
            filteredMonths.find(
              m => m.value === selectedValues.endMonth.value
            ) || filteredMonths[0];

          endMonthDropdown.innerHTML = filteredMonths
            .map(
              month =>
                `<div class="dropdown-item ${
                  month.value === newEndMonth.value ? 'selected' : ''
                }" data-value="${month.value}">${month.label}</div>`
            )
            .join('');

          if (filteredMonths.length > 0) {
            selectedValues.endMonth = newEndMonth;
            updateDropdown(
              'endMonthSelect',
              newEndMonth.value,
              newEndMonth.label
            );
          }

          // Reattach event listeners para las nuevas opciones
          endMonthDropdown.querySelectorAll('.dropdown-item').forEach(item => {
            item.addEventListener('click', () => {
              const value = parseInt(item.getAttribute('data-value') || '0');
              const month = monthsToUse.find(m => m.value === value);
              if (month) {
                selectedValues.endMonth = month;
                updateDropdown('endMonthSelect', value, month.label);
                closeAllDropdowns();
              }
            });
          });
        };

        // Función para mostrar/ocultar el campo "Hasta"
        const toggleEndMonthField = () => {
          const selectMonthLabel = document.getElementById('selectMonthLabel');
          const startMonthLabel = document.getElementById('startMonthLabel');
          const endMonthContainer = document.getElementById(
            'endMonthContainer'
          );

          if (!selectMonthLabel || !startMonthLabel || !endMonthContainer)
            return;

          const isMultipleMonths = selectedValues.periodType.value === 'months';
          selectMonthLabel.style.display = isMultipleMonths ? 'none' : 'block';
          startMonthLabel.style.display = isMultipleMonths ? 'block' : 'none';
          endMonthContainer.style.display = isMultipleMonths ? 'block' : 'none';

          if (isMultipleMonths) {
            updateEndMonthOptions();
          }
        };

        // Event listeners para los botones de dropdown
        const yearButton = document.querySelector(
          '[data-dropdown="yearSelect"]'
        ) as HTMLElement;
        const periodTypeButton = document.querySelector(
          '[data-dropdown="periodTypeSelect"]'
        ) as HTMLElement;
        const startMonthButton = document.querySelector(
          '[data-dropdown="startMonthSelect"]'
        ) as HTMLElement;
        const endMonthButton = document.querySelector(
          '[data-dropdown="endMonthSelect"]'
        ) as HTMLElement;

        // Verificar que los elementos existen antes de agregar event listeners
        if (yearButton) {
          yearButton.addEventListener('click', () =>
            handleDropdownClick(yearButton, 'yearDropdown')
          );
        }

        if (periodTypeButton) {
          periodTypeButton.addEventListener('click', () =>
            handleDropdownClick(periodTypeButton, 'periodTypeDropdown')
          );
        }

        if (startMonthButton) {
          startMonthButton.addEventListener('click', () =>
            handleDropdownClick(startMonthButton, 'startMonthDropdown')
          );
        }

        if (endMonthButton) {
          endMonthButton.addEventListener('click', () =>
            handleDropdownClick(endMonthButton, 'endMonthDropdown')
          );
        }

        // Event listeners para las opciones
        document
          .querySelectorAll('#yearDropdown .dropdown-item')
          .forEach(item => {
            item.addEventListener('click', () => {
              const value = parseInt(item.getAttribute('data-value') || '0');
              const year = years.find(y => y.value === value);
              if (year) {
                selectedValues.year = year;
                updateDropdown('yearSelect', value, year.label);
                // Actualizar las opciones de meses cuando cambia el año
                updateStartMonthOptions(value);
                closeAllDropdowns();
              }
            });
          });

        document
          .querySelectorAll('#periodTypeDropdown .dropdown-item')
          .forEach(item => {
            item.addEventListener('click', () => {
              const value = item.getAttribute('data-value') || '';
              const periodType = consolidatedTypes.find(p => p.value === value);
              if (periodType) {
                selectedValues.periodType = periodType;
                updateDropdown('periodTypeSelect', value, periodType.label);
                toggleEndMonthField();
                closeAllDropdowns();
              }
            });
          });

        document
          .querySelectorAll('#startMonthDropdown .dropdown-item')
          .forEach(item => {
            item.addEventListener('click', () => {
              const value = parseInt(item.getAttribute('data-value') || '0');
              const month = months.find(m => m.value === value);
              if (month) {
                selectedValues.startMonth = month;
                updateDropdown('startMonthSelect', value, month.label);
                if (selectedValues.periodType.value === 'months') {
                  updateEndMonthOptions();
                }
                closeAllDropdowns();
              }
            });
          });

        // Cerrar dropdowns al hacer click fuera
        document.addEventListener('click', e => {
          if (!(e.target as HTMLElement).closest('.custom-dropdown')) {
            closeAllDropdowns();
          }
        });

        // Inicializar la vista
        toggleEndMonthField();

        // Almacenar selectedValues globalmente para preConfirm
        (window as any).modalSelectedValues = selectedValues;
      },
      preConfirm: () => {
        const selectedValues = (window as any).modalSelectedValues;

        // Validación para asegurar que los valores requeridos estén presentes
        if (
          !selectedValues.year ||
          !selectedValues.periodType ||
          !selectedValues.startMonth
        ) {
          Swal.showValidationMessage(t('report_page:select_fields'));
          return false;
        }

        // Validación adicional para período de múltiples meses
        if (
          selectedValues.periodType.value === 'months' &&
          !selectedValues.endMonth
        ) {
          Swal.showValidationMessage(t('report_page:select_end_month'));
          return false;
        }

        return {
          year: selectedValues.year,
          periodType: selectedValues.periodType,
          startMonth: selectedValues.startMonth,
          endMonth: selectedValues.endMonth
        };
      }
    }).then(async result => {
      if (result.isConfirmed && result.value) {
        const { year, periodType, startMonth, endMonth } = result.value;

        // Mostrar loading
        Swal.fire({
          title: t('report_page:generating'),
          html: t('report_page:please_wait'),
          allowOutsideClick: false,
          didOpen: () => {
            Swal.showLoading();
          }
        });

        try {
          const { data } = await getReport(
            currentUser?.token,
            '0',
            'CONSOLIDATED',
            undefined, // sendEmail
            undefined, // month for other report types
            year?.value?.toString(),
            startMonth?.value?.toString(),
            periodType?.value === 'months'
              ? endMonth?.value?.toString()
              : startMonth?.value?.toString()
          );

          if (data.data) {
            Swal.fire({
              icon: 'success',
              title: t('report_page:generated'),
              text: t('report_page:success_email'),
              confirmButtonColor: '#1976d2'
            });
            setSuccessAlertVisible(true);
          } else {
            throw new Error(t('report_page:error'));
          }
        } catch (error) {
          Swal.fire({
            icon: 'error',
            title: t('report_page:error'),
            text: t('report_page:error_page'),
            confirmButtonColor: '#1976d2'
          });
          setUpdateError(t('report_page:error_page'));
        }
      }
    });
  };

  const onSubmit = async (typeReport: string, sendEmail?: string) => {
    if (typeReport === 'PREVIEW_EMPLOYEE' && !validEmailEmployee) {
      validationEmailRegex(emailEmployee, 'EMPLOYEES');
      return false;
    }

    const period = isPeriodReport(typeReport) ? periods[typeReport] : undefined;
    if (period && !isPeriodComplete(period)) {
      setUpdateError(t('report_page:period_incomplete'));
      return false;
    }

    const loaderSelected = loader as any;
    const loaderType = {
      ...loaderSelected,
      [typeReport]: true
    };
    setLoader(loaderType);

    const loaderTypeFalse = {
      ...loaderSelected,
      [typeReport]: false
    };

    try {
      const { data } = await getReport(
        currentUser?.token,
        '0',
        typeReport,
        sendEmail,
        typeReport === 'PARTICIPATIONS_BY_DATE'
          ? month.value
          : typeReport === 'MONTHLY_AMBASSADORS'
          ? monthAmbassador.value
          : undefined,
        typeReport === 'PARTICIPATIONS_BY_DATE' || typeReport === 'CONSOLIDATED'
          ? year.value
          : typeReport === 'MONTHLY_AMBASSADORS'
          ? yearAmbassador.value
          : undefined,
        typeReport === 'CONSOLIDATED' ? startMonthCon.value : undefined,
        typeReport === 'CONSOLIDATED' ? endMonthCon.value : undefined,
        period
      );
      processResponse(data.data);
    } catch (e: any) {
      // Sin esto el spinner quedaba girando si la request fallaba (p.ej. el
      // 400 de un período inválido).
      const message = e?.response?.data?.data;
      setUpdateError(
        typeof message === 'string' ? message : t('report_page:error_page')
      );
    } finally {
      setLoader(loaderTypeFalse);
    }
  };

  const validationEmailRegex = (email: any, typeContent: any) => {
    const validationEmail = regextValidationEmail(email);
    if (typeContent === 'EMPLOYEES') {
      setEmailEmployee(email);
      setValidEmailEmployee(validationEmail);
    } else {
      setUpdateError('typeContent error');
    }
  };

  const processResponse = (data: any) => {
    if (data) {
      setSuccessAlertVisible(true);
    } else {
      setUpdateError(t('report_page:error_page'));
    }
  };

  const regextValidationEmail = (email: string) => {
    const regex = new RegExp(emailRegex);
    return regex.test(email);
  };

  const handleCheck = (value: any) => {
    value ? setCheck(true) : setCheck(false);
    if (value && emailEmployee != '') {
      const validation = regextValidationEmail(emailEmployee);
      if (!validation) {
        setValidEmailEmployee(true);
      } else {
        setValidEmailEmployee(false);
      }
    } else {
      setValidEmailEmployee(false);
    }
  };

  const confirmAlert = (check: any) => {
    let typeButton: string;
    if (check) {
      typeButton = 'PREVIEW_EMPLOYEE';
    } else {
      typeButton = 'EMPLOYEES';
    }
    Swal.fire({
      text: t('report_page:send_report'),
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: t('group_page:yes'),
      cancelButtonText: t('group_page:not'),
      reverseButtons: true
    }).then(result => {
      if (result.isConfirmed) {
        onSubmit(typeButton, check ? emailEmployee : undefined);
      }
    });
  };

  useEffect(() => {
    if (
      endMonthCon &&
      months.findIndex(m => m.value === endMonthCon.value) <
        months.findIndex(m => m.value === startMonthCon.value)
    ) {
      const valid = months.find(m => m.value === startMonthCon.value);
      if (valid) setEndtMonthCon(valid);
    }
  }, [startMonthCon]);

  return (
    <>
      {updateError && (
        <ErrorAlert
          text={updateError}
          allowClose={true}
          onClose={() => setUpdateError(null)}
        />
      )}
      {successAlertVisible && (
        <SuccessAlert
          text={t('report_page:success_email')}
          allowClose={true}
          onClose={() => setSuccessAlertVisible(false)}
        />
      )}
      <SectionWrapper>
        <h1>{t('report_page:reports')}</h1>
        <SectionCard>
          <BackgroundImg className="purpure-bck">
            <h3>
              {t('report_page:report_of')}
              <strong>{t('report_page:report_cosolidated')}</strong>
            </h3>
          </BackgroundImg>
          <ContentBlock>
            <MarginDescription>
              <p>
                <strong>{t('report_page:participation_info')}</strong>{' '}
                {t('report_page:participation_ambassadors')}
                <span
                  style={{
                    right: '-5px',
                    backgroundColor: '#7ed321',
                    color: 'white',
                    fontSize: '10px',
                    padding: '2px 6px',
                    borderRadius: '10px',
                    fontWeight: 'bold',
                    textTransform: 'uppercase',
                    marginLeft: '7px'
                  }}
                >
                  Nuevo
                </span>
              </p>
            </MarginDescription>
            <MarginButtonDownload>
              <div className="iconDownload" onClick={showReportModal}>
                {!loader.CONSOLIDATED ? (
                  <img alt="iconEmail" src={iconEmail} />
                ) : (
                  <Spinner name="sign-up" />
                )}
              </div>
            </MarginButtonDownload>
          </ContentBlock>
        </SectionCard>
        <SectionCard>
          <BackgroundImg className="blue-bck">
            <h3>
              {t('report_page:report_of')}
              <strong>{t('report_page:ambassadors_report')}</strong>
            </h3>
          </BackgroundImg>
          <ContentBlock>
            <MarginDescription>
              <p>
                <strong>{t('report_page:download_all_ambassadors')}</strong>
              </p>
              <p className="subTitleAmbassadors">
                {t('report_page:registered_ambassadors')}
              </p>
              <FiltersForm>
                <PeriodSelector
                  value={periods.AMBASSADORS}
                  onChange={onChangePeriod('AMBASSADORS')}
                />
              </FiltersForm>
            </MarginDescription>
            <MarginButtonDownload>
              <div
                className="iconDownload"
                onClick={() => onSubmit('AMBASSADORS')}
              >
                {!loader.AMBASSADORS ? (
                  <img alt="iconEmail" src={iconEmail} />
                ) : (
                  <Spinner name="sign-up" />
                )}
              </div>
            </MarginButtonDownload>
          </ContentBlock>
        </SectionCard>
        <SectionCard>
          <BackgroundImg className="purpure-bck">
            <h3>
              {t('report_page:report_of')}
              <strong>{t('report_page:platform_report')}</strong>
            </h3>
          </BackgroundImg>
          <ContentBlock>
            <MarginDescription>
              <p>
                <strong>{t('report_page:download_all_the_information')}</strong>
              </p>
              <p>{t('report_page:participating_ambassadors')}</p>
              <FiltersForm>
                <PeriodSelector
                  value={periods.PLATFORM}
                  onChange={onChangePeriod('PLATFORM')}
                />
              </FiltersForm>
            </MarginDescription>
            <MarginButtonDownload>
              <div
                className="iconDownload"
                onClick={() => onSubmit('PLATFORM', emailPlatform)}
              >
                {!loader.PLATFORM ? (
                  <img alt="iconEmail" src={iconEmail} />
                ) : (
                  <Spinner name="sign-up" />
                )}
              </div>
            </MarginButtonDownload>
          </ContentBlock>
        </SectionCard>
        <SectionCard>
          <BackgroundImg className="blue-bck">
            <h3>
              {t('report_page:report_of')}
              <strong>{t('report_page:report_challenge')}</strong>
            </h3>
          </BackgroundImg>
          <ContentBlock>
            <MarginDescription>
              <p>
                <strong>{t('report_page:download_all_challenges')}</strong>
              </p>
              <p>{t('report_page:participating_challenges')}</p>
              <FiltersForm>
                <PeriodSelector
                  value={periods.CREATOR}
                  onChange={onChangePeriod('CREATOR')}
                />
              </FiltersForm>
            </MarginDescription>
            <MarginButtonDownload>
              <div className="iconDownload" onClick={() => onSubmit('CREATOR')}>
                {!loader.CREATOR ? (
                  <img alt="iconEmail" src={iconEmail} />
                ) : (
                  <Spinner name="sign-up" />
                )}
              </div>
            </MarginButtonDownload>
          </ContentBlock>
        </SectionCard>
        <SectionCard>
          <BackgroundImg className="purpure-bck">
            <h3>
              {t('report_page:report_of')}
              <strong>{t('report_page:bonus_report')}</strong>
            </h3>
          </BackgroundImg>
          <ContentBlock>
            <MarginDescription>
              <p>
                <strong>{t('report_page:download_bonus')}</strong>
              </p>
              <p>{t('report_page:bonus_description')}</p>
              <FiltersForm>
                <PeriodSelector
                  value={periods.BONUS}
                  onChange={onChangePeriod('BONUS')}
                />
              </FiltersForm>
            </MarginDescription>
            <MarginButtonDownload>
              <div className="iconDownload" onClick={() => onSubmit('BONUS')}>
                {!loader.BONUS ? (
                  <img alt="iconEmail" src={iconEmail} />
                ) : (
                  <Spinner name="sign-up" />
                )}
              </div>
            </MarginButtonDownload>
          </ContentBlock>
        </SectionCard>
        <SectionCard>
          <BackgroundImg className="blue-bck">
            <h3>
              {t('report_page:report_month')}
              <strong>{t('report_page:ambassadors_report')}</strong>
            </h3>
          </BackgroundImg>
          <ContentBlock>
            <MarginDescription>
              <p>
                <strong>{t('report_page:month_report')}</strong>
              </p>
              <FiltersForm>
                <DropdownSelect
                  onSelect={e => {
                    setMonthAmbassador(e);
                  }}
                  value={monthAmbassador}
                  defaultValue={monthAmbassador}
                  options={getAvailableMonths(yearAmbassador.value)}
                />
                <DropdownSelect
                  onSelect={e => {
                    setYearAmbassador(e);
                  }}
                  value={yearAmbassador}
                  defaultValue={yearAmbassador}
                  options={years}
                />
              </FiltersForm>
            </MarginDescription>
            <MarginButtonDownload>
              <div
                className="iconDownload"
                onClick={() =>
                  onSubmit('MONTHLY_AMBASSADORS', emailParticipation)
                }
              >
                {!loader.MONTHLY_AMBASSADORS ? (
                  <img alt="iconEmail" src={iconEmail} />
                ) : (
                  <Spinner name="sign-up" />
                )}
              </div>
            </MarginButtonDownload>
          </ContentBlock>
        </SectionCard>
        {company?.company_permission.allowReportParticipation && (
          <SectionCard>
            <BackgroundImg className="purpure-bck">
              <h3>
                {t('report_page:report_of')}
                <strong>{t('report_page:participation_report')}</strong>
              </h3>
            </BackgroundImg>
            <ContentBlock>
              <MarginDescription>
                <p>
                  <strong>{t('report_page:participation_info')}</strong>{' '}
                  {t('report_page:participation_ambassadors')}
                </p>
                <FiltersForm>
                  <DropdownSelect
                    onSelect={e => {
                      setMonth(e);
                    }}
                    value={month}
                    defaultValue={month}
                    options={getAvailableMonths(year.value)}
                  />
                  <DropdownSelect
                    onSelect={e => {
                      setYear(e);
                    }}
                    value={year}
                    defaultValue={year}
                    options={years}
                  />
                </FiltersForm>
              </MarginDescription>
              <MarginButtonDownload>
                <div
                  className="iconDownload"
                  onClick={() =>
                    onSubmit('PARTICIPATIONS_BY_DATE', emailParticipation)
                  }
                >
                  {!loader.PARTICIPATIONS_BY_DATE ? (
                    <img alt="iconEmail" src={iconEmail} />
                  ) : (
                    <Spinner name="sign-up" />
                  )}
                </div>
              </MarginButtonDownload>
            </ContentBlock>
          </SectionCard>
        )}
        <SectionCard>
          <BackgroundImg className="blue-bck">
            <h3>
              {t('report_page:send_email_of')}
              <strong>{t('report_page:ambassador_month')}</strong>
            </h3>
          </BackgroundImg>
          <ContentBlock>
            <MarginDescription>
              <p className="titleSendAmbassadors">
                <strong>{t('report_page:send_all_ambassadors')}</strong>
              </p>
              <CheckboxContainer>
                <TextCheckbox>{t('report_page:test_mail')} </TextCheckbox>
                <Input
                  type="checkbox"
                  onChange={e => {
                    handleCheck(e.target.checked);
                  }}
                  checked={check}
                />
                <Input
                  className="inputEmail"
                  type={check ? 'text' : 'hidden'}
                  value={emailEmployee}
                  onChange={e => {
                    validationEmailRegex(e.target.value, 'EMPLOYEES');
                  }}
                  placeholder={t('example')}
                />
              </CheckboxContainer>
              <p className="textDespriction" hidden={!check}>
                {t('report_page:from_email')}
              </p>
              {check && !validEmailEmployee && (
                <ErrorSpan id="error_message_report">
                  {t('report_page:error_send_email')}
                </ErrorSpan>
              )}
            </MarginDescription>
            <MarginButtonDownload>
              <div className="iconDownload" onClick={() => confirmAlert(check)}>
                {!loader.EMPLOYEES || !loader.PREVIEW_EMPLOYEE ? (
                  <img alt="iconEmail" src={iconEmail} />
                ) : (
                  <Spinner name="sign-up" />
                )}
              </div>
            </MarginButtonDownload>
          </ContentBlock>
        </SectionCard>
      </SectionWrapper>
    </>
  );
};

export default ReportsAdminPage;
